import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Ticket, Calendar, MapPin, Download, Home, ArrowRight, Mail } from 'lucide-react';
import { Button, Card } from '../components/ui';
import Footer from '../components/Footer';

const PaymentSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as any;

  const order = state?.order;
  const orderItems = state?.orderItems || [];
  const paymentMethod = state?.paymentMethod || 'card';
  const total = state?.total || 0;
  const promoCode = state?.promoCode;

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const formatCurrency = (amount: number) =>
    `${amount.toFixed(2)} JOD`;

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'card': return 'Credit/Debit Card';
      case 'applePay': return 'Apple Pay';
      case 'googlePay': return 'Google Pay';
      case 'crypto': return 'Crypto';
      case 'cash': return 'Cash on Delivery';
      default: return method;
    }
  };

  return (
    <div className="min-h-screen theme-transition relative overflow-hidden" style={{ background: 'var(--liquid-gradient-bg)' }}>
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-96 h-96 liquid-glass opacity-20 liquid-animate-morph"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 liquid-glass-strong opacity-15 liquid-animate-float"></div>
        <div className="absolute top-1/2 left-1/3 w-64 h-64 liquid-glass opacity-10 liquid-animate-wave"></div>
      </div>

      <div className="relative z-10 pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 liquid-glass-strong rounded-full mb-6 animate-scale-in">
              <CheckCircle size={48} className="text-green-400" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-3 liquid-glow-strong" style={{ color: 'var(--liquid-text-primary)' }}>
              {t('payment.success.title', 'Payment Successful!')}
            </h1>
            <p className="text-lg" style={{ color: 'var(--liquid-text-muted)' }}>
              {paymentMethod === 'cash'
                ? t('payment.success.cashDesc', 'Your order has been placed. Tickets will be delivered within 24-48 hours.')
                : t('payment.success.desc', 'Your tickets have been confirmed and sent to your email.')}
            </p>
          </div>

          {/* Order Summary Card */}
          <Card variant="glass" className="p-6 mb-6">
            <div className="flex items-center mb-4">
              <Ticket size={20} className="text-yellow-400 mr-3" />
              <h2 className="text-xl font-bold" style={{ color: 'var(--liquid-text-primary)' }}>
                {t('payment.success.orderSummary', 'Order Summary')}
              </h2>
            </div>

            {/* Order ID */}
            {order?.id && (
              <div className="flex justify-between items-center py-2 border-b border-white/10 mb-4">
                <span className="text-sm" style={{ color: 'var(--liquid-text-muted)' }}>Order ID</span>
                <span className="font-mono text-sm font-semibold" style={{ color: 'var(--liquid-text-primary)' }}>
                  #{order.id.slice(-8).toUpperCase()}
                </span>
              </div>
            )}

            {/* Items */}
            <div className="space-y-3 mb-4">
              {orderItems.map((item: any, index: number) => (
                <div key={index} className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-sm" style={{ color: 'var(--liquid-text-primary)' }}>
                      {item.eventTitle}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--liquid-text-muted)' }}>
                      {item.ticketType} × {item.quantity}
                    </p>
                  </div>
                  <span className="font-semibold text-sm ml-4" style={{ color: 'var(--liquid-text-primary)' }}>
                    {formatCurrency(item.price * item.quantity)}
                  </span>
                </div>
              ))}
            </div>

            {/* Promo Code */}
            {promoCode && (
              <div className="flex justify-between items-center py-2 border-t border-white/10">
                <span className="text-sm text-green-400">Promo: {promoCode}</span>
                <span className="text-sm text-green-400">Applied ✓</span>
              </div>
            )}

            {/* Total */}
            <div className="flex justify-between items-center pt-3 border-t border-white/10">
              <span className="font-bold text-lg" style={{ color: 'var(--liquid-text-primary)' }}>
                {t('checkout.total', 'Total Paid')}
              </span>
              <span className="font-bold text-xl text-green-400">
                {formatCurrency(total)}
              </span>
            </div>

            {/* Payment Method */}
            <div className="flex justify-between items-center pt-2">
              <span className="text-sm" style={{ color: 'var(--liquid-text-muted)' }}>Payment Method</span>
              <span className="text-sm font-medium" style={{ color: 'var(--liquid-text-primary)' }}>
                {getPaymentMethodLabel(paymentMethod)}
              </span>
            </div>
          </Card>

          {/* What's Next Card */}
          <Card variant="glass" className="p-6 mb-6">
            <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--liquid-text-primary)' }}>
              {t('payment.success.whatsNext', "What's Next?")}
            </h2>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full liquid-glass flex items-center justify-center flex-shrink-0">
                  <Mail size={16} className="text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-sm" style={{ color: 'var(--liquid-text-primary)' }}>
                    {t('payment.success.emailStep', 'Check your email')}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--liquid-text-muted)' }}>
                    {t('payment.success.emailDesc', 'Your ticket confirmation and QR code have been sent to your email.')}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full liquid-glass flex items-center justify-center flex-shrink-0">
                  <Download size={16} className="text-yellow-400" />
                </div>
                <div>
                  <p className="font-medium text-sm" style={{ color: 'var(--liquid-text-primary)' }}>
                    {t('payment.success.downloadStep', 'Download your ticket')}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--liquid-text-muted)' }}>
                    {t('payment.success.downloadDesc', 'Go to My Tickets to download your ticket PDF.')}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full liquid-glass flex items-center justify-center flex-shrink-0">
                  <Calendar size={16} className="text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-sm" style={{ color: 'var(--liquid-text-primary)' }}>
                    {t('payment.success.attendStep', 'Attend the event')}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--liquid-text-muted)' }}>
                    {t('payment.success.attendDesc', 'Show your QR code at the entrance for quick check-in.')}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="glass"
              size="lg"
              onClick={() => navigate('/tickets')}
              className="flex-1"
              icon={<Ticket size={20} />}
            >
              {t('payment.success.viewTickets', 'View My Tickets')}
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={() => navigate('/')}
              className="flex-1"
              icon={<Home size={20} />}
            >
              {t('payment.success.browseMore', 'Browse More Events')}
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;
